import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, Globe, ChevronDown, LogOut, Package, Settings, LayoutDashboard, LayoutGrid, MessageSquare, FileText } from 'lucide-react';
import { LogoMark } from '../Logo';
import { MegaMenu } from './MegaMenu';
import { SearchAutocomplete } from '../SearchAutocomplete';
import { UnreadMessagesBadge } from '../UnreadMessagesBadge';
import { CurrencySelector } from '../CurrencySelector';
import { ShipToCountryBanner } from '../ShipToCountryBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useLanguage, LANGUAGE_OPTIONS } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';

export const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin, isVendor } = useAuth();
  const { itemCount } = useCart();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border/40 shadow-sm">
      {/* Top Bar */}
      <div className="bg-[#0F4C5C] text-white py-2">
        <div className="container-main flex justify-between items-center text-sm gap-4">
          <p className="hidden sm:block flex-shrink-0">{t('freeShipping')}</p>
          <ShipToCountryBanner language={language} />
          <div className="flex items-center gap-4 ml-auto flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 hover:text-[#E9C46A] transition-colors">
                  <Globe className="w-4 h-4" />
                  <span>{language.toUpperCase()}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                {LANGUAGE_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.code}
                    onClick={() => setLanguage(opt.code)}
                    className={language === opt.code ? 'bg-accent' : ''}
                    data-testid={`lang-option-${opt.code}`}
                  >
                    <span className="mr-2">{opt.flag}</span>
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="container-main py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 group -ml-2" data-testid="logo-link">
            <LogoMark size={60} theme="teal" className="transition-transform group-hover:scale-[1.02]" />
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden md:flex flex-1 max-w-xl mx-8">
            <SearchAutocomplete />
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Currency */}
            <div className="hidden sm:block">
              <CurrencySelector />
            </div>
            {/* Messages */}
            <UnreadMessagesBadge />

            {/* Wishlist */}
            {isAuthenticated && (
              <Link to="/wishlist" className="btn-ghost relative" data-testid="wishlist-link">
                <Heart className="w-5 h-5" />
              </Link>
            )}

            {/* Cart */}
            <Link to="/cart" className="btn-ghost relative" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-[#E9C46A] text-[#0F4C5C] text-xs font-bold">
                  {itemCount}
                </Badge>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-[#0F4C5C]/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#0F4C5C]" />
                    </div>
                    <span className="hidden sm:block font-medium">{user?.first_name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-dashboard-link">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {t('adminDashboard')}
                    </DropdownMenuItem>
                  )}
                  
                  {isVendor && (
                    <DropdownMenuItem onClick={() => navigate('/vendor')} data-testid="vendor-dashboard-link">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {t('vendorDashboard')}
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="orders-link">
                    <Package className="w-4 h-4 mr-2" />
                    {t('myOrders')}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem onClick={() => navigate('/quotes')} data-testid="quotes-link">
                    <FileText className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Mes devis' : 'My quotes'}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="messages-link">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Messages' : 'Messages'}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="account-link">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('account')}
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-button">
                  {t('login')}
                </Button>
                <Button className="btn-primary hidden sm:flex" onClick={() => navigate('/register')} data-testid="register-button">
                  {t('register')}
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="md:hidden mt-4">
          <div className="relative">
            <Input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-4 pr-12 h-10 rounded-full"
            />
            <Button
              type="submit"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-[#0F4C5C] h-8 w-8 p-0"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </nav>

      {/* Category Navigation */}
      <div className="border-t border-border/40 bg-white/50 hidden md:block">
        <div className="container-main">
          <nav className="flex items-center gap-6 py-3">
            <MegaMenu />
            <Link to="/shop" className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap" data-testid="shop-link">
              {t('shop')}
            </Link>
            <Link to="/shop?category=electronique" className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap">
              {t('navElectronics')}
            </Link>
            <Link to="/shop?category=mode-femme" className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap">
              {t('navFashion')}
            </Link>
            <Link to="/shop?category=maison-deco" className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap">
              {t('navHomeCategory')}
            </Link>
            <Link to="/shop?category=beaute" className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap">
              {t('navBeauty')}
            </Link>
            <Link to="/categories" className="text-xs font-semibold uppercase tracking-widest text-[#0F4C5C]/50 hover:text-[#E9C46A] transition-colors whitespace-nowrap flex items-center gap-1.5" data-testid="all-categories-link">
              <LayoutGrid className="w-3 h-3" />
              {t('navViewAll')}
            </Link>
            <a
              href="https://www.blog.respectfulmarkets.com"
              className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap"
              data-testid="blog-link"
            >
              Blog
            </a>
            <div className="ml-auto flex items-center gap-6">
              {!isVendor && (
                <Link to="/become-seller" className="text-sm font-medium text-[#2A9D8F] hover:text-[#0F4C5C] transition-colors whitespace-nowrap" data-testid="become-seller-link">
                  {t('becomeASeller')}
                </Link>
              )}
              <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-[#0F4C5C] transition-colors whitespace-nowrap" data-testid="pricing-link">
                {t('pricing')}
              </Link>
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/40 bg-white">
          <nav className="container-main py-4 space-y-2">
            <Link to="/shop" className="block py-2 text-foreground hover:text-[#0F4C5C]" onClick={() => setMobileMenuOpen(false)}>
              {t('shop')}
            </Link>
            <Link to="/categories" className="flex items-center gap-2 py-2 text-foreground hover:text-[#0F4C5C] font-semibold" onClick={() => setMobileMenuOpen(false)}>
              <LayoutGrid className="w-4 h-4" />
              {t('navAllCategories')}
            </Link>
            <Link to="/shop?category=electronique" className="block py-2 text-foreground hover:text-[#0F4C5C]" onClick={() => setMobileMenuOpen(false)}>
              {t('navElectronics')}
            </Link>
            <Link to="/shop?category=mode-femme" className="block py-2 text-foreground hover:text-[#0F4C5C]" onClick={() => setMobileMenuOpen(false)}>
              {t('navFashion')}
            </Link>
            <Link to="/shop?category=maison-deco" className="block py-2 text-foreground hover:text-[#0F4C5C]" onClick={() => setMobileMenuOpen(false)}>
              {t('navHomeCategory')}
            </Link>
            <Link to="/shop?category=beaute" className="block py-2 text-foreground hover:text-[#0F4C5C]" onClick={() => setMobileMenuOpen(false)}>
              {t('navBeauty')}
            </Link>
            {!isAuthenticated && (
              <div className="pt-4 border-t border-border/40 flex gap-2">
                <Button className="flex-1" variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                  {t('login')}
                </Button>
                <Button className="flex-1 btn-primary" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>
                  {t('register')}
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
